import { Federation, Gender } from '@prisma/client'
import { getGeocode } from 'use-places-autocomplete'
import { Country } from 'components/common/LocaleContext'
import { AttendeeFormDataAddress } from 'domain/models/AttendeeFormData/AttendeeFormDataParticulars'

const genderMap: { [key: string]: Gender } = {
  H: Gender.MALE,
  HOMME: Gender.MALE,
  M: Gender.MALE,
  MASCULIN: Gender.MALE,
  MONSIEUR: Gender.MALE,
  GARÇON: Gender.MALE,
  G: Gender.MALE,
  F: Gender.FEMALE,
  FEMME: Gender.FEMALE,
  FÉMININ: Gender.FEMALE,
  MADAME: Gender.FEMALE,
  MLLE: Gender.FEMALE,
  FILLE: Gender.FEMALE
}

export const convertGender = (input: string): Gender | undefined => {
  if (!input) return undefined
  const normalizedInput = input.trim().toUpperCase()
  return genderMap[normalizedInput]
}

const isFederationKey = (input: string): input is keyof typeof Federation =>
  !!Object.keys(Federation).find((key) => key === input)

export const convertFederation = (input: string): Federation | undefined => {
  if (!input) return undefined
  const normalizedInput = input.trim()

  return isFederationKey(normalizedInput)
    ? Federation[normalizedInput]
    : undefined
}

export const convertCountry = (
  input: string,
  countriesList: Country[]
): string | undefined => {
  if (!input) return undefined
  const normalizedInput = input.trim().toUpperCase()
  const countryKey = countriesList.find(
    (country) =>
      country.code.toUpperCase() === normalizedInput ||
      country.name.toUpperCase() === normalizedInput
  )?.code
  return countryKey
}

export const convertAddress = async (
  input: string
): Promise<AttendeeFormDataAddress> => {
  const normalizedInput = input.trim()
  const [address] = await getGeocode({
    address: normalizedInput
  })

  return {
    postalAddressMain: address?.formatted_address!,
    postalAddressSecondary: '',
    ...convertGeocoderAddress(address?.address_components)
  }
}

export const convertGeocoderAddress = (
  addressComponent: google.maps.GeocoderAddressComponent[] | undefined
): Omit<
  AttendeeFormDataAddress,
  'postalAddressMain' | 'postalAddressSecondary'
> => {
  return {
    postalAddressZipcode:
      addressComponent?.find((comp) => comp.types.includes('postal_code'))
        ?.long_name ?? '',
    postalAddressCity:
      addressComponent?.find((comp) => comp.types.includes('locality'))
        ?.long_name ??
      addressComponent?.find((comp) => comp.types.includes('postal_town'))
        ?.long_name ??
      '',
    postalAddressCountry:
      addressComponent?.find((comp) => comp.types.includes('country'))
        ?.long_name ?? '',
    postalAddressAreaLevel1:
      addressComponent?.find((comp) =>
        comp.types.includes('administrative_area_level_1')
      )?.long_name ?? '',
    postalAddressAreaLevel2:
      addressComponent?.find((comp) =>
        comp.types.includes('administrative_area_level_2')
      )?.long_name ?? ''
  }
}
